import { useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams, useSearchParams } from 'react-router-dom'
import { formatDistanceToNow, format } from 'date-fns'
import { inboxStore, InboxMessage, InboxChannel } from '@/stores/InboxStore'
import { authStore } from '@/stores/AuthStore'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import FileUpload, { UploadedFile } from '@/components/inbox/FileUpload'
import CodeBlock from '@/components/inbox/CodeBlock'
import { RichTextEditor } from '@/components/inbox/RichTextEditor'
import { EditMessageModal } from '@/components/inbox/EditMessageModal'
import { PinnedMessagesPanel } from '@/components/inbox/PinnedMessagesPanel'
import { MessageSearch } from '@/components/inbox/MessageSearch'
import api from '@/services/api'
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
  CpuChipIcon,
  BellIcon,
  HashtagIcon,
  PaperClipIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InboxIcon,
  Cog6ToothIcon,
  DocumentIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline'
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid'

const ProjectInboxPage = observer(() => {
  const { projectId } = useParams<{ projectId?: string }>()
  const [searchParams] = useSearchParams()
  const workspaceId = searchParams.get('workspace') || ''

  const [messageInput, setMessageInput] = useState('')
  const [sending, setSending] = useState(false)
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const [showFileUpload, setShowFileUpload] = useState(false)
  const [pendingAttachments, setPendingAttachments] = useState<UploadedFile[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messageInputRef = useRef<HTMLTextAreaElement>(null)
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null)

  // New state for inbox enhancements
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingMessage, setEditingMessage] = useState<InboxMessage | null>(null)
  const [showPinnedPanel, setShowPinnedPanel] = useState(false)
  const [showSearchPanel, setShowSearchPanel] = useState(false)
  // Track mentions for the message being composed (for future use in sending mentions with message)
  const [_mentions, setMentions] = useState<{ userIds: string[]; agentIds: string[]; channelIds: string[] }>({
    userIds: [], agentIds: [], channelIds: []
  })

  // Load channels
  useEffect(() => {
    if (projectId && workspaceId) {
      inboxStore.fetchProjectChannels(projectId)
    } else if (workspaceId) {
      inboxStore.fetchUserChannels(workspaceId)
    }
  }, [projectId, workspaceId])

  // Load messages when channel changes
  useEffect(() => {
    if (inboxStore.selectedChannelId) {
      inboxStore.fetchMessages(inboxStore.selectedChannelId)
      inboxStore.markChannelRead(inboxStore.selectedChannelId)
    }
  }, [inboxStore.selectedChannelId])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [inboxStore.messages.length])

  const handleSendMessage = async () => {
    if ((!messageInput.trim() && pendingAttachments.length === 0) || !inboxStore.selectedChannelId || !inboxStore.currentProjectId) return

    setSending(true)
    try {
      // Include attachments in the message
      const attachments = pendingAttachments.map(att => ({
        id: att.id,
        filename: att.original_filename,
        mime_type: att.mime_type,
        file_size: att.file_size,
        storage_path: att.storage_path
      }))

      await inboxStore.sendMessage(
        inboxStore.selectedChannelId,
        inboxStore.currentProjectId,
        messageInput.trim() || `Shared ${pendingAttachments.length} file${pendingAttachments.length > 1 ? 's' : ''}`,
        { attachments: attachments.length > 0 ? attachments : undefined }
      )
      setMessageInput('')
      setPendingAttachments([])
      setShowFileUpload(false)
      messageInputRef.current?.focus()
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setSending(false)
    }
  }

  const handleFileUploadComplete = (file: UploadedFile) => {
    setPendingAttachments(prev => [...prev, file])
  }

  const removePendingAttachment = (id: string) => {
    setPendingAttachments(prev => prev.filter(att => att.id !== id))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleHITLResponse = async (message: InboxMessage, optionValue: string) => {
    try {
      await inboxStore.respondToHITL(message.id, { selected: optionValue })
    } catch (error) {
      console.error('Failed to respond:', error)
    }
  }

  const toggleProject = (projectId: string) => {
    const newExpanded = new Set(expandedProjects)
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId)
    } else {
      newExpanded.add(projectId)
    }
    setExpandedProjects(newExpanded)
  }

  // Handle editing a message
  const handleEditMessage = (message: InboxMessage) => {
    setEditingMessage(message)
    setShowEditModal(true)
  }

  const handleSaveEdit = async (messageId: string, newContent: string) => {
    await api.put(`/inbox/messages/${messageId}`, { content: newContent })
    // Refresh messages to show the edit
    if (inboxStore.selectedChannelId) {
      inboxStore.fetchMessages(inboxStore.selectedChannelId)
    }
  }

  // Handle pinning/unpinning a message
  const handlePinMessage = async (messageId: string) => {
    await api.post(`/inbox/messages/${messageId}/pin`)
    if (inboxStore.selectedChannelId) {
      inboxStore.fetchMessages(inboxStore.selectedChannelId)
    }
  }

  const handleUnpinMessage = async (messageId: string) => {
    await api.delete(`/inbox/messages/${messageId}/pin`)
    if (inboxStore.selectedChannelId) {
      inboxStore.fetchMessages(inboxStore.selectedChannelId)
    }
  }

  // Scroll to a specific message and highlight it temporarily
  const scrollToMessage = (messageId: string) => {
    // Wait for messages to load
    setTimeout(() => {
      const messageElement = document.getElementById(`message-${messageId}`)
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Highlight the message temporarily
        setHighlightedMessageId(messageId)
        setTimeout(() => setHighlightedMessageId(null), 2000)
      }
    }, 300) // Small delay to allow messages to render
  }

  // Handle search result click - navigate to channel and scroll to message
  const handleSearchResultClick = (messageId: string, channelId: string) => {
    // If already in this channel, just scroll to message
    if (inboxStore.selectedChannelId === channelId) {
      scrollToMessage(messageId)
    } else {
      // Switch channels first, then scroll after messages load
      inboxStore.selectChannel(channelId)
      // Wait for channel to load then scroll
      setTimeout(() => scrollToMessage(messageId), 500)
    }
    setShowSearchPanel(false)
  }

  // Group channels by project
  const channelsByProject = inboxStore.channels.reduce((acc, channel) => {
    if (!acc[channel.project_id]) {
      acc[channel.project_id] = []
    }
    acc[channel.project_id].push(channel)
    return acc
  }, {} as Record<string, InboxChannel[]>)

  const renderMessageContent = (message: InboxMessage) => {
    // Render code blocks if present using Monaco Editor
    if (message.code_blocks && message.code_blocks.length > 0) {
      return (
        <div className="space-y-3">
          {message.content && (
            <p className="whitespace-pre-wrap">{message.content}</p>
          )}
          {message.code_blocks.map((block, index) => (
            <CodeBlock
              key={index}
              code={block.code}
              language={block.language}
              filename={block.filename}
              maxHeight={300}
              minHeight={60}
            />
          ))}
        </div>
      )
    }

    return <p className="whitespace-pre-wrap">{message.content}</p>
  }

  const renderHITLOptions = (message: InboxMessage) => {
    if (!message.requires_response || message.user_response || !message.decision_options) {
      return null
    }

    const isExpired = message.response_deadline && new Date(message.response_deadline) < new Date()

    if (isExpired) {
      return (
        <div className="mt-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
          <div className="flex items-center gap-2 text-red-600">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Response deadline has passed</span>
          </div>
        </div>
      )
    }

    return (
      <div className="mt-3 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
        <div className="flex items-center gap-2 text-yellow-600 mb-2">
          <ClockIcon className="h-5 w-5" />
          <span className="text-sm font-medium">Action Required</span>
          {message.response_deadline && (
            <span className="text-xs text-yellow-600">
              Due {formatDistanceToNow(new Date(message.response_deadline), { addSuffix: true })}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {message.decision_options.map((option, index) => (
            <Button
              key={index}
              size="sm"
              variant="secondary"
              onClick={() => handleHITLResponse(message, option.value)}
              title={option.description}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  const renderMessage = (message: InboxMessage) => {
    const isUser = message.sender_type === 'user'
    const isAgent = message.sender_type === 'agent'
    const isSystem = message.sender_type === 'system'
    const isHITL = message.message_type === 'hitl_request'
    const isStatusUpdate = message.message_type === 'status_update'
    const isError = message.message_type === 'error_alert'

    // Check if this message is highlighted
    const isHighlighted = highlightedMessageId === message.id

    // System/status messages
    if (isSystem || isStatusUpdate) {
      return (
        <div
          key={message.id}
          id={`message-${message.id}`}
          className={`flex justify-center my-2 transition-all duration-500 ${
            isHighlighted ? 'bg-yellow-500/15 dark:bg-yellow-900/30 rounded-lg py-2' : ''
          }`}
        >
          <div className={`px-3 py-1 rounded-full text-xs ${
            isError ? 'bg-red-500/15 text-red-600' : 'bg-muted text-muted-foreground'
          }`}>
            {message.content}
          </div>
        </div>
      )
    }

    return (
      <div
        key={message.id}
        id={`message-${message.id}`}
        className={`flex gap-3 mb-4 group transition-all duration-500 ${
          isUser ? 'flex-row-reverse' : ''
        } ${isHighlighted ? 'bg-yellow-500/15 dark:bg-yellow-900/30 rounded-lg p-2 -mx-2' : ''}`}
      >
        {/* Avatar */}
        <div className={`flex-shrink-0 ${isUser ? 'order-2' : ''}`}>
          {isAgent ? (
            <div className="w-8 h-8 rounded-full bg-purple-500/15 flex items-center justify-center">
              <CpuChipIcon className="h-5 w-5 text-purple-600" />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full bg-blue-500/15 flex items-center justify-center">
              <UserCircleIcon className="h-5 w-5 text-blue-600" />
            </div>
          )}
        </div>

        {/* Message bubble */}
        <div className={`flex-1 max-w-[80%] ${isUser ? 'order-1' : ''}`}>
          <div className={`rounded-lg p-3 ${
            isUser
              ? 'bg-blue-600 text-white ml-auto'
              : isHITL
                ? 'bg-yellow-500/10 border border-yellow-500/20'
                : 'bg-muted text-foreground'
          }`}>
            {/* Sender info */}
            {!isUser && (
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-muted-foreground">
                  {isAgent ? 'Agent' : 'System'}
                </span>
                {message.priority === 'high' || message.priority === 'urgent' && (
                  <Badge variant="error" size="sm">
                    {message.priority}
                  </Badge>
                )}
              </div>
            )}

            {/* Content */}
            {renderMessageContent(message)}

            {/* HITL options */}
            {isHITL && renderHITLOptions(message)}

            {/* Response if answered */}
            {message.user_response && (
              <div className="mt-2 p-2 bg-green-500/10 rounded border border-green-500/20">
                <div className="flex items-center gap-1 text-green-600 text-sm">
                  <CheckIcon className="h-4 w-4" />
                  <span>Responded: {JSON.stringify(message.user_response.selected)}</span>
                </div>
              </div>
            )}

            {/* Attachments indicator */}
            {message.has_attachments && message.attachments && (
              <div className="mt-2 flex flex-wrap gap-2">
                {message.attachments.map((att, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs"
                  >
                    <PaperClipIcon className="h-3 w-3" />
                    <span>{(att as {filename?: string}).filename || 'Attachment'}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Reactions */}
            {message.reactions && Object.keys(message.reactions).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(message.reactions).map(([emoji, users]) => (
                  <button
                    key={emoji}
                    onClick={() => {
                      if (users.includes(authStore.user?.id || '')) {
                        inboxStore.removeReaction(message.id, emoji)
                      } else {
                        inboxStore.addReaction(message.id, emoji)
                      }
                    }}
                    className={`px-2 py-0.5 rounded-full text-sm border ${
                      users.includes(authStore.user?.id || '')
                        ? 'bg-blue-500/15 border-blue-300'
                        : 'bg-muted/50 border-border'
                    }`}
                  >
                    {emoji} {users.length}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Timestamp, edit/pin buttons, and thread info */}
          <div className={`flex items-center gap-2 mt-1 text-xs text-muted-foreground ${isUser ? 'justify-end' : ''}`}>
            <span>{format(new Date(message.created_at), 'HH:mm')}</span>
            {(message as any).is_edited && (
              <span className="text-muted-foreground">(edited)</span>
            )}
            {message.reply_count > 0 && (
              <button
                onClick={() => inboxStore.fetchThreadMessages(message.id)}
                className="text-blue-500 hover:text-blue-600"
              >
                {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
              </button>
            )}
            {/* Action buttons */}
            <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Pin/Unpin button */}
              <button
                onClick={() => (message as any).is_pinned
                  ? handleUnpinMessage(message.id)
                  : handlePinMessage(message.id)
                }
                className={`p-1 rounded hover:bg-muted dark:hover:bg-muted ${
                  (message as any).is_pinned ? 'text-yellow-500' : 'text-muted-foreground'
                }`}
                title={(message as any).is_pinned ? 'Unpin message' : 'Pin message'}
              >
                {(message as any).is_pinned ? (
                  <BookmarkSolidIcon className="h-4 w-4" />
                ) : (
                  <BookmarkIcon className="h-4 w-4" />
                )}
              </button>
              {/* Edit button (only for own messages) */}
              {isUser && message.sender_user_id === authStore.user?.id && (
                <button
                  onClick={() => handleEditMessage(message)}
                  className="p-1 rounded hover:bg-muted dark:hover:bg-muted text-muted-foreground"
                  title="Edit message"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex">
      {/* Channel Sidebar */}
      <div className="w-64 border-r border-border bg-muted/50 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              <InboxIcon className="h-5 w-5" />
              Project Inbox
            </h2>
            <button
              onClick={() => inboxStore.toggleNotifications()}
              className="relative p-1 text-muted-foreground hover:text-foreground"
            >
              <BellIcon className="h-5 w-5" />
              {inboxStore.unreadNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                  {inboxStore.unreadNotificationCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Channel List */}
        <div className="flex-1 overflow-y-auto p-2">
          {Object.entries(channelsByProject).map(([projId, channels]) => (
            <div key={projId} className="mb-2">
              {/* Project header */}
              <button
                onClick={() => toggleProject(projId)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-foreground hover:bg-muted rounded"
              >
                {expandedProjects.has(projId) ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
                <span className="truncate">Project {projId.slice(0, 8)}...</span>
              </button>

              {/* Project channels */}
              {expandedProjects.has(projId) && (
                <div className="ml-4 space-y-0.5">
                  {channels.map(channel => {
                    const isSelected = channel.id === inboxStore.selectedChannelId
                    const unread = inboxStore.unreadCounts[channel.id] || 0

                    return (
                      <button
                        key={channel.id}
                        onClick={() => inboxStore.selectChannel(channel.id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm ${
                          isSelected
                            ? 'bg-blue-500/15 text-blue-600'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {channel.is_project_channel ? (
                          <HashtagIcon className="h-4 w-4" />
                        ) : (
                          <CpuChipIcon className="h-4 w-4" />
                        )}
                        <span className="flex-1 truncate text-left">{channel.name}</span>
                        {unread > 0 && (
                          <span className="w-5 h-5 bg-blue-500 rounded-full text-xs text-white flex items-center justify-center">
                            {unread}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          ))}

          {inboxStore.channels.length === 0 && !inboxStore.channelLoading && (
            <div className="text-center py-8 text-muted-foreground">
              <ChatBubbleLeftRightIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No channels yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {inboxStore.selectedChannel ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                {inboxStore.selectedChannel.is_project_channel ? (
                  <HashtagIcon className="h-6 w-6 text-muted-foreground" />
                ) : (
                  <CpuChipIcon className="h-6 w-6 text-purple-500" />
                )}
                <div>
                  <h3 className="font-semibold text-foreground">
                    {inboxStore.selectedChannel.name}
                  </h3>
                  {inboxStore.selectedChannel.description && (
                    <p className="text-sm text-muted-foreground">
                      {inboxStore.selectedChannel.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Search button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSearchPanel(!showSearchPanel)}
                  title="Search messages"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </Button>
                {/* Pinned messages button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPinnedPanel(!showPinnedPanel)}
                  title="View pinned messages"
                >
                  <BookmarkIcon className="h-5 w-5" />
                </Button>
                <Button variant="ghost" size="sm">
                  <Cog6ToothIcon className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4">
              {inboxStore.messagesLoading && inboxStore.messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Loading messages...
                </div>
              ) : inboxStore.messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <ChatBubbleLeftRightIcon className="h-12 w-12 mb-2 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start the conversation!</p>
                </div>
              ) : (
                <>
                  {inboxStore.hasMoreMessages && (
                    <div className="text-center mb-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const oldestMessage = inboxStore.messages[0]
                          if (oldestMessage) {
                            inboxStore.fetchMessages(
                              inboxStore.selectedChannelId!,
                              oldestMessage.id
                            )
                          }
                        }}
                        disabled={inboxStore.messagesLoading}
                      >
                        Load older messages
                      </Button>
                    </div>
                  )}
                  {inboxStore.messages.map(renderMessage)}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-border">
              {/* Typing indicator */}
              {inboxStore.typingUsers[inboxStore.selectedChannelId!]?.length > 0 && (
                <div className="text-xs text-muted-foreground mb-2">
                  Someone is typing...
                </div>
              )}

              {/* File upload area */}
              {showFileUpload && inboxStore.selectedChannelId && inboxStore.currentProjectId && (
                <div className="mb-3 p-3 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">Attach Files</span>
                    <button
                      onClick={() => setShowFileUpload(false)}
                      className="text-muted-foreground hover:text-muted-foreground"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <FileUpload
                    channelId={inboxStore.selectedChannelId}
                    projectId={inboxStore.currentProjectId}
                    onUploadComplete={handleFileUploadComplete}
                    onError={(error) => console.error('Upload error:', error)}
                  />
                </div>
              )}

              {/* Pending attachments display */}
              {pendingAttachments.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {pendingAttachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center gap-2 px-2 py-1 bg-blue-500/10 rounded-lg border border-blue-500/20"
                    >
                      <DocumentIcon className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-blue-600 truncate max-w-32">
                        {att.original_filename}
                      </span>
                      <button
                        onClick={() => removePendingAttachment(att.id)}
                        className="text-blue-400 hover:text-blue-600"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  {/* Rich Text Editor with @mentions and emoji support */}
                  {inboxStore.currentProjectId && workspaceId && (
                    <RichTextEditor
                      value={messageInput}
                      onChange={setMessageInput}
                      onMentionsChange={setMentions}
                      projectId={inboxStore.currentProjectId}
                      workspaceId={workspaceId}
                      placeholder="Type a message... Use @ to mention users/agents, # for channels"
                      minHeight={60}
                      maxHeight={200}
                      disabled={sending}
                      onSubmit={handleSendMessage}
                    />
                  )}
                  {/* Fallback simple textarea if IDs not available */}
                  {(!inboxStore.currentProjectId || !workspaceId) && (
                    <textarea
                      ref={messageInputRef}
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type a message..."
                      rows={1}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                      style={{ minHeight: '40px', maxHeight: '120px' }}
                    />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setShowFileUpload(!showFileUpload)}
                    className={`p-2 rounded-lg transition-colors ${
                      showFileUpload || pendingAttachments.length > 0
                        ? 'bg-blue-500/15 text-blue-600'
                        : 'text-muted-foreground hover:bg-muted hover:text-muted-foreground'
                    }`}
                    title="Attach files"
                  >
                    <PaperClipIcon className="h-5 w-5" />
                  </button>
                  <Button
                    onClick={handleSendMessage}
                    disabled={(!messageInput.trim() && pendingAttachments.length === 0) || sending}
                  >
                    <PaperAirplaneIcon className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Select a channel to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* Thread Panel */}
      {inboxStore.selectedThreadId && (
        <div className="w-80 border-l border-border flex flex-col">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Thread</h3>
            <button
              onClick={() => inboxStore.closeThread()}
              className="p-1 text-muted-foreground hover:text-muted-foreground"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {inboxStore.threadLoading ? (
              <div className="text-center text-muted-foreground">Loading...</div>
            ) : (
              inboxStore.threadMessages.map(renderMessage)
            )}
          </div>
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Reply in thread..."
                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && (e.target as HTMLInputElement).value.trim()) {
                    const value = (e.target as HTMLInputElement).value.trim()
                    inboxStore.sendMessage(
                      inboxStore.selectedChannelId!,
                      inboxStore.currentProjectId!,
                      value,
                      { parent_message_id: inboxStore.selectedThreadId! }
                    )
                    ;(e.target as HTMLInputElement).value = ''
                  }
                }}
              />
              <Button size="sm">
                <PaperAirplaneIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Panel */}
      {inboxStore.showNotifications && (
        <div className="absolute right-4 top-20 w-80 bg-card rounded-lg shadow-xl border border-border max-h-96 overflow-y-auto z-50">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Notifications</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => inboxStore.markAllNotificationsRead()}
                className="text-xs text-blue-600 hover:text-blue-600"
              >
                Mark all read
              </button>
              <button
                onClick={() => inboxStore.toggleNotifications()}
                className="p-1 text-muted-foreground hover:text-muted-foreground"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {inboxStore.notifications.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No notifications
              </div>
            ) : (
              inboxStore.notifications.map(notification => (
                <div
                  key={notification.id}
                  onClick={() => inboxStore.markNotificationRead(notification.id)}
                  className={`p-3 cursor-pointer hover:bg-muted/50 ${
                    !notification.is_read ? 'bg-blue-500/10' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 ${
                      !notification.is_read ? 'bg-blue-500' : 'bg-transparent'
                    }`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {notification.title}
                      </p>
                      {notification.body && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Edit Message Modal */}
      <EditMessageModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setEditingMessage(null)
        }}
        message={editingMessage}
        onSave={handleSaveEdit}
      />

      {/* Pinned Messages Panel */}
      {inboxStore.selectedChannelId && (
        <PinnedMessagesPanel
          isOpen={showPinnedPanel}
          onClose={() => setShowPinnedPanel(false)}
          channelId={inboxStore.selectedChannelId}
          onMessageClick={(messageId) => {
            setShowPinnedPanel(false)
            scrollToMessage(messageId)
          }}
          onUnpin={handleUnpinMessage}
        />
      )}

      {/* Search Panel */}
      {showSearchPanel && inboxStore.currentProjectId && (
        <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setShowSearchPanel(false)}>
          <div
            className="absolute right-0 top-0 h-full w-96 bg-card dark:bg-background shadow-xl z-50 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border dark:border-border">
              <h2 className="text-lg font-semibold text-foreground dark:text-muted-foreground">
                Search Messages
              </h2>
              <button
                onClick={() => setShowSearchPanel(false)}
                className="p-1 hover:bg-muted dark:hover:bg-card rounded"
              >
                <XMarkIcon className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-hidden">
              <MessageSearch
                projectId={inboxStore.currentProjectId}
                onResultClick={handleSearchResultClick}
                className="h-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default ProjectInboxPage
